import currency from "currency.js";
import { useTranslation } from "react-i18next";
import { Placement } from "react-bootstrap/esm/types";
import { useMemo } from "react";
import { GetFarmBoost200Response } from "../../../codegen-api";
import { ExplanationWrapper, Stat, StatNumber, StatTitle } from "./style";
import TooltipExplanation from "../Tooltip";
import { TooltipWrapper } from "../Header/AccountModal/style";
import { ICON_COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { ReactComponent as Info } from "../../../assets/svg/info.svg";
import { BoostValueWrapper } from "../../TradeForm/style";

export type AnnouncementType = "alert" | "feature";

interface IAnnouncementProp {
  tradeFormVolume?: string;
  tradeFormBoostedVolume?: string;
  boostIncrease?: string;
  boostIncreasePercentage?: string;
  farmData?: GetFarmBoost200Response;
  placement?: Placement;
  isTradeForm?: boolean;
}

function BoostedVolumeTooltip({
  tradeFormVolume,
  tradeFormBoostedVolume,
  boostIncrease,
  boostIncreasePercentage,
  farmData,
  placement = "top",
  isTradeForm = true,
}: IAnnouncementProp) {
  const { t } = useTranslation("app", {
    keyPrefix: "shared.BoostedVolumeTooltip",
  });

  const totalVolume = useMemo(() => {
    if (!farmData) {
      return 0;
    }
    if (Number(farmData.farm_boost_avg) <= 0) {
      return 0;
    }
    return Number(farmData.boosted_volume) / Number(farmData.farm_boost_avg);
  }, [farmData]);
  const tooltipExplanation = useMemo(
    () => (
      <ExplanationWrapper>
        <span>
          {isTradeForm
            ? t("boosted_volume_description1_order")
            : t("boosted_volume_description1_trade")}
          <span style={{ color: TEXT_COLORS.one }}>
            {isTradeForm
              ? t("boosted_volume_description2_order")
              : t("boosted_volume_description2_trade")}
          </span>
          {isTradeForm
            ? t("boosted_volume_description3_order")
            : t("boosted_volume_description3_trade")}
        </span>
        <Stat>
          <StatTitle>{t("volume")}</StatTitle>
          <StatNumber>
            {farmData ? currency(tradeFormVolume || totalVolume).format() : "-"}
          </StatNumber>
        </Stat>
        <Stat>
          <StatTitle>
            {!isTradeForm ? t("avg_farm_boost") : t("farm_boost")}
          </StatTitle>
          <StatNumber>
            {farmData
              ? `${currency(
                  !isTradeForm
                    ? farmData.farm_boost_avg
                    : Number(farmData.farm_boost) + Number(boostIncrease || 0),
                  {
                    symbol: "",
                  }
                ).format()}x`
              : "-"}
            {boostIncreasePercentage && (
              <BoostValueWrapper>{`+${Number(boostIncreasePercentage).toFixed(
                2
              )}%`}</BoostValueWrapper>
            )}
          </StatNumber>
        </Stat>
        <Stat>
          <StatTitle>{t("boosted_volume")}</StatTitle>
          <StatNumber>
            {farmData
              ? currency(
                  tradeFormBoostedVolume || farmData.boosted_volume
                ).format()
              : "-"}
          </StatNumber>
        </Stat>
      </ExplanationWrapper>
    ),
    [
      boostIncrease,
      boostIncreasePercentage,
      tradeFormBoostedVolume,
      farmData,
      isTradeForm,
      t,
      totalVolume,
      tradeFormVolume,
    ]
  );

  return (
    <TooltipExplanation
      placement={placement}
      title={isTradeForm ? t("boosted_volume") : t("total_boosted_volume")}
      explanation={tooltipExplanation}
      renderContent={({ ref, ...triggerHandler }) => (
        <TooltipWrapper $stroke={ICON_COLORS.two} ref={ref} {...triggerHandler}>
          <Info />
        </TooltipWrapper>
      )}
    />
  );
}

export default BoostedVolumeTooltip;
